import React from 'react';
import './StarRating.css';

interface StarRatingProps {
  rating: number;
  maxRating?: number;
  className?: string;
  onChange?: (rating: number) => void;
}

const StarRating: React.FC<StarRatingProps> = ({
  rating,
  maxRating = 5,
  className = '',
  onChange
}) => {
  // Round to nearest 0.5
  const roundedRating = Math.round(rating * 2) / 2;
  
  // Generate aria label for accessibility
  const ariaLabel = `Rating: ${roundedRating} out of ${maxRating} stars`;
  
  // Handle star click
  const handleStarClick = (selectedRating: number) => {
    if (onChange) {
      onChange(selectedRating);
    }
  };
  
  return (
    <div
      className={`star-rating-container ${className}`}
      role={onChange ? 'radiogroup' : 'img'}
      aria-label={ariaLabel}
      title={`${rating.toFixed(1)} out of ${maxRating}`}
    >
      {[...Array(maxRating)].map((_, index) => {
        const starValue = index + 1;
        let starClass = 'star-empty';
        
        // Standard logic for determining which stars should be filled
        if (roundedRating >= starValue) {
          starClass = 'star-filled';
        } else if (roundedRating >= starValue - 0.5) {
          starClass = 'star-half';
        }
        
        return (
          <span
            key={index}
            className={`star ${starClass} ${onChange ? 'interactive' : ''}`}
            onClick={() => onChange && handleStarClick(starValue)}
            role={onChange ? 'radio' : undefined}
            aria-checked={onChange && roundedRating >= starValue}
            tabIndex={onChange ? 0 : undefined}
            onKeyPress={(e) => {
              if (onChange && (e.key === 'Enter' || e.key === ' ')) {
                handleStarClick(starValue);
              }
            }}
          >
            ★
          </span>
        );
      })}
    </div>
  );
};

export default StarRating;
